/* eslint-disable react/require-default-props */
/* eslint-disable react/jsx-boolean-value */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Input,
  InputAdornment,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import PropTypes from 'prop-types';

import api from '../../../services/api';

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
const AUDIO_MIME_TYPES = [
  'audio/mp3', 'audio/aac', 'audio/ogg', 'audio/wav', 'audio/aiff', 'audio/wma', 'audio/mpeg',
];
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const DEFAULT_FORM = {
  name: '',
  categoryId: '',
  description: '',
  radius: 10,
  beaconId: '',
  latitude: '',
  longitude: '',
  isVisible: true,
  isAutomaticallyPlayed: true,
  linkURL: '',
  linkTitle: '',
};

export default function PointFormModal({
  open,
  onClose,
  type,
  categories,
  onCreated,
  pointToEdit,
  onSaved,
  initialCoords,
}) {
  const isBeacon = type === 'beacon';
  const isEditMode = !!pointToEdit;

  const [beacons, setBeacons] = useState([]);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [audioFile, setAudioFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  // URLs blob locais das mídias JÁ existentes no ponto sendo editado —
  // baixadas via API autenticada pra mostrar preview sem exigir novo upload.
  const [existingAudioUrl, setExistingAudioUrl] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const audioInputId = 'point-form-modal-audio-input';
  const imageInputId = 'point-form-modal-image-input';

  useEffect(() => {
    if (!open || !isBeacon) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const response = await api.get('/beacons');
        if (cancelled) return;
        const sorted = [...response.data].sort((a, b) => (
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        ));
        setBeacons(sorted);
      } catch (err) {
        console.error(err);
      }
    })();

    return () => { cancelled = true; };
  }, [open, isBeacon]);

  // Reset form quando o modal fecha; pré-preenche em modo edit.
  useEffect(() => {
    if (!open) {
      setFormData(DEFAULT_FORM);
      setAudioFile(null);
      setImageFile(null);
      // Revoga blob URLs pra não vazar memória.
      if (existingAudioUrl) URL.revokeObjectURL(existingAudioUrl);
      if (existingImageUrl) URL.revokeObjectURL(existingImageUrl);
      setExistingAudioUrl(null);
      setExistingImageUrl(null);
      return;
    }

    if (pointToEdit) {
      setFormData({
        name: pointToEdit.name || '',
        categoryId: pointToEdit.categoryId || '',
        description: pointToEdit.description || '',
        radius: pointToEdit.radius ?? 10,
        beaconId: pointToEdit.beaconId || '',
        latitude: pointToEdit.latitude?.toString() || '',
        longitude: pointToEdit.longitude?.toString() || '',
        isVisible: pointToEdit.isVisible ?? true,
        isAutomaticallyPlayed: pointToEdit.isAutomaticallyPlayed ?? true,
        linkURL: pointToEdit.linkURL || '',
        linkTitle: pointToEdit.linkTitle || '',
      });
    } else if (initialCoords && (initialCoords.lat || initialCoords.lng)) {
      // Modo criação vindo de clique no mapa — pré-preenche coords.
      setFormData((prev) => ({
        ...prev,
        latitude: initialCoords.lat?.toString() || '',
        longitude: initialCoords.lng?.toString() || '',
      }));
    }
  }, [open, pointToEdit, initialCoords]);

  // Baixa mídia existente do ponto (audio + image) via API autenticada
  // pra mostrar preview quando o modal abre em edit mode.
  useEffect(() => {
    if (!open || !pointToEdit) return undefined;
    let cancelled = false;

    const loadMedia = async (path, setUrl) => {
      if (!path) return;
      try {
        const res = await api.get(`/points/file/${path}`, { responseType: 'blob' });
        if (cancelled) return;
        const blob = new Blob([res.data], { type: res.headers['content-type'] });
        setUrl(URL.createObjectURL(blob));
      } catch (err) {
        console.warn(`PointFormModal: falha ao baixar ${path}`, err);
      }
    };

    if (pointToEdit.audioURL) loadMedia(pointToEdit.audioURL, setExistingAudioUrl);
    if (pointToEdit.imageURL) loadMedia(pointToEdit.imageURL, setExistingImageUrl);

    return () => { cancelled = true; };
  }, [open, pointToEdit?.audioURL, pointToEdit?.imageURL]);

  const sortedCategories = useMemo(() => (
    [...(categories || [])].sort((a, b) => (
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    ))
  ), [categories]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    const input = document.getElementById(imageInputId);

    if (!file) {
      setImageFile(null);
      if (input) input.value = '';
      return;
    }

    if (!IMAGE_MIME_TYPES.includes(file.type)) {
      setImageFile(null);
      if (input) input.value = '';
      alert('Selecione um arquivo de imagem válido (jpg, jpeg, png, webp, gif).');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setImageFile(null);
      if (input) input.value = '';
      alert(`A imagem deve ter no máximo ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }

    setImageFile(file);
  };

  const handleImageRemoval = () => {
    setImageFile(null);
    const input = document.getElementById(imageInputId);
    if (input) input.value = '';
  };

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    const input = document.getElementById(audioInputId);

    if (!file) {
      setAudioFile(null);
      if (input) input.value = '';
      return;
    }

    if (!AUDIO_MIME_TYPES.includes(file.type)) {
      setAudioFile(null);
      if (input) input.value = '';
      alert('Selecione um arquivo de áudio válido (mp3, aac, ogg, wav, aiff, wma, mpeg).');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setAudioFile(null);
      if (input) input.value = '';
      alert(`O áudio deve ter no máximo ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }

    setAudioFile(file);
  };

  const handleAudioRemoval = () => {
    setAudioFile(null);
    const input = document.getElementById(audioInputId);
    if (input) input.value = '';
  };

  const validate = () => {
    if (!formData.name.trim()) return 'Informe um nome para o ponto.';
    if (!formData.categoryId) return 'Selecione uma categoria.';

    if (isBeacon) {
      if (!formData.beaconId) return 'Selecione um beacon.';
    } else {
      const lat = Number(formData.latitude);
      const lng = Number(formData.longitude);
      const radius = Number(formData.radius);
      if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
        return 'Latitude inválida (entre -90 e 90).';
      }
      if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
        return 'Longitude inválida (entre -180 e 180).';
      }
      if (!Number.isFinite(radius) || radius < 1) {
        return 'Raio deve ser pelo menos 1 metro.';
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    const data = {
      name: formData.name.trim(),
      type,
      latitude: isBeacon ? 0 : Number(formData.latitude),
      longitude: isBeacon ? 0 : Number(formData.longitude),
      description: formData.description,
      categoryId: formData.categoryId,
      radius: isBeacon ? 0 : Number(formData.radius),
      beaconId: isBeacon ? formData.beaconId : undefined,
      isVisible: formData.isVisible === true || formData.isVisible === 'true',
      isAutomaticallyPlayed: formData.isAutomaticallyPlayed === true || formData.isAutomaticallyPlayed === 'true',
      linkURL: formData.linkURL,
      linkTitle: formData.linkTitle,
    };

    setSubmitting(true);
    try {
      const saved = isEditMode
        ? (await api.patch(`/points/${pointToEdit.id}`, data)).data
        : (await api.post('/points', data)).data;

      const formatted = {
        id: saved.id,
        name: saved.name,
        latitude: saved.latitude,
        longitude: saved.longitude,
        description: saved.description,
        isActive: saved.isActive,
        createdAt: saved.createdAt,
        updatedAt: saved.updatedAt,
        categoryId: saved.category?.id ?? saved.categoryId,
        category: saved.category?.name,
        type: saved.type,
        radius: saved.radius,
        beaconId: saved.beaconId,
        isVisible: saved.isVisible,
        isAutomaticallyPlayed: saved.isAutomaticallyPlayed,
        linkURL: saved.linkURL,
        linkTitle: saved.linkTitle,
        audioURL: pointToEdit?.audioURL || '',
        imageURL: pointToEdit?.imageURL || '',
      };

      if (audioFile) {
        const audioForm = new FormData();
        audioForm.append('files', audioFile);
        try {
          const r = await api.patch(`/points/file/audio/${saved.id}`, audioForm);
          formatted.audioURL = r.data.audioURL;
        } catch (audioErr) {
          console.warn('Ponto salvo, mas falhou o upload do áudio', audioErr);
        }
      }

      if (imageFile) {
        const imageForm = new FormData();
        imageForm.append('files', imageFile);
        try {
          const r = await api.patch(`/points/file/image/${saved.id}`, imageForm);
          formatted.imageURL = r.data.imageURL;
        } catch (imageErr) {
          console.warn('Ponto salvo, mas falhou o upload da imagem', imageErr);
        }
      }

      if (isEditMode) {
        if (onSaved) onSaved(formatted);
        alert('Ponto atualizado com sucesso.');
      } else {
        if (onCreated) onCreated(formatted);
        alert('Ponto registrado com sucesso.');
      }
      onClose();
    } catch (err2) {
      console.error(err2);
      alert(err2?.response?.data?.message || `Falha ao ${isEditMode ? 'atualizar' : 'registrar'} o ponto.`);
    } finally {
      setSubmitting(false);
    }
  };

  let title;
  if (isEditMode) title = isBeacon ? 'Editar ponto beacon' : 'Editar ponto GPS';
  else title = isBeacon ? 'Novo ponto beacon' : 'Novo ponto GPS (por coordenadas)';
  const contextHint = isBeacon
    ? 'Beacons são detectados por proximidade Bluetooth. As coordenadas GPS não são necessárias.'
    : 'Preencha as coordenadas manualmente. Você pode obter latitude e longitude clicando com o botão direito num ponto do Google Maps.';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            {contextHint}
          </Typography>

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              name="name"
              label="Nome"
              type="text"
              value={formData.name}
              placeholder="Digite um nome para o ponto"
              onChange={handleChange}
              required
              inputProps={{ maxLength: 100 }}
            />
          </FormControl>

          <FormControl fullWidth sx={{ my: 1 }}>
            <InputLabel id="pfm-cat-label">Categoria</InputLabel>
            <Select
              name="categoryId"
              labelId="pfm-cat-label"
              label="Categoria"
              value={formData.categoryId || ''}
              onChange={handleChange}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return <span style={{ color: '#B5B5B5' }}>Selecionar...</span>;
                const c = sortedCategories.find((x) => x.id === selected);
                return c ? c.name : '';
              }}
            >
              {sortedCategories.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              name="description"
              label="Descrição"
              type="text"
              multiline
              rows={4}
              value={formData.description}
              placeholder="Descreva do que se trata o ponto"
              onChange={handleChange}
              inputProps={{ maxLength: 500 }}
            />
          </FormControl>

          {!isBeacon && (
            <>
              <FormControl
                fullWidth
                sx={{
                  my: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1,
                }}
              >
                <TextField
                  name="latitude"
                  label="Latitude"
                  type="number"
                  value={formData.latitude}
                  onChange={handleChange}
                  required
                  inputProps={{ step: 'any', min: -90, max: 90 }}
                  helperText="Ex.: -23.547360"
                />
                <TextField
                  name="longitude"
                  label="Longitude"
                  type="number"
                  value={formData.longitude}
                  onChange={handleChange}
                  required
                  inputProps={{ step: 'any', min: -180, max: 180 }}
                  helperText="Ex.: -46.636005"
                />
              </FormControl>

              <FormControl fullWidth sx={{ my: 1 }}>
                <TextField
                  name="radius"
                  label="Raio (metros)"
                  type="number"
                  value={formData.radius}
                  onChange={handleChange}
                  required
                  inputProps={{ min: 1, max: 200 }}
                  helperText="Distância na qual o ponto será ativado."
                />
              </FormControl>
            </>
          )}

          {isBeacon && (
            <FormControl fullWidth sx={{ my: 1 }}>
              <InputLabel id="pfm-beacon-label">Beacon</InputLabel>
              <Select
                name="beaconId"
                labelId="pfm-beacon-label"
                label="Beacon"
                value={formData.beaconId || ''}
                onChange={handleChange}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return <span style={{ color: '#B5B5B5' }}>Selecionar...</span>;
                  const b = beacons.find((x) => x.id === selected);
                  return b ? b.name : '';
                }}
              >
                {beacons.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl
            fullWidth
            sx={{
              my: 1,
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Input
              id={audioInputId}
              type="file"
              accept="audio/*"
              onChange={handleAudioChange}
            />
            {audioFile && (
              <Button
                size="large"
                variant="outlined"
                color="error"
                onClick={handleAudioRemoval}
              >
                Remover
              </Button>
            )}
          </FormControl>
          {audioFile && (
            <div style={{ marginTop: 10, marginBottom: 10 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Prévia do novo áudio:</Typography>
              <audio controls src={URL.createObjectURL(audioFile)} style={{ width: '100%' }}>
                <track kind="captions" />
              </audio>
            </div>
          )}
          {!audioFile && existingAudioUrl && (
            <div style={{ marginTop: 10, marginBottom: 10 }}>
              <Typography variant="body2" sx={{ mb: 0.5, color: '#666' }}>
                Áudio atual (selecione um novo arquivo pra substituir):
              </Typography>
              <audio controls src={existingAudioUrl} style={{ width: '100%' }}>
                <track kind="captions" />
              </audio>
            </div>
          )}

          <FormControl fullWidth sx={{ my: 1 }}>
            <InputLabel id="pfm-auto-label">Reprodução automática do áudio</InputLabel>
            <Select
              name="isAutomaticallyPlayed"
              labelId="pfm-auto-label"
              label="Reprodução automática do áudio"
              value={formData.isAutomaticallyPlayed}
              onChange={handleChange}
            >
              <MenuItem value={true}>Sim</MenuItem>
              <MenuItem value={false}>Não</MenuItem>
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            sx={{
              my: 1,
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Input
              id={imageInputId}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
            {imageFile && (
              <Button
                size="large"
                variant="outlined"
                color="error"
                onClick={handleImageRemoval}
              >
                Remover
              </Button>
            )}
          </FormControl>
          {imageFile && (
            <div style={{ marginTop: 10, marginBottom: 10 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Prévia da nova imagem:</Typography>
              <img
                src={URL.createObjectURL(imageFile)}
                alt="Prévia"
                style={{ maxWidth: '100%' }}
              />
            </div>
          )}
          {!imageFile && existingImageUrl && (
            <div style={{ marginTop: 10, marginBottom: 10 }}>
              <Typography variant="body2" sx={{ mb: 0.5, color: '#666' }}>
                Imagem atual (selecione um novo arquivo pra substituir):
              </Typography>
              <img
                src={existingImageUrl}
                alt="Imagem atual"
                style={{ maxWidth: '100%' }}
              />
            </div>
          )}

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              name="linkURL"
              label="Link URL"
              type="url"
              value={formData.linkURL}
              placeholder="Digite um link para o ponto"
              helperText="Ex.: https://www.google.com.br"
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon />
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              name="linkTitle"
              label="Título para o link"
              type="text"
              value={formData.linkTitle}
              placeholder="Digite um título para o link"
              helperText="Ex.: Google"
              onChange={handleChange}
              inputProps={{ maxLength: 100 }}
            />
          </FormControl>

          <FormControl fullWidth sx={{ my: 1 }}>
            <InputLabel id="pfm-vis-label">Visibilidade</InputLabel>
            <Select
              name="isVisible"
              labelId="pfm-vis-label"
              label="Visibilidade"
              value={formData.isVisible}
              onChange={handleChange}
            >
              <MenuItem value={true}>Visível</MenuItem>
              <MenuItem value={false}>Invisível</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={submitting}
          >
            {(() => {
              if (submitting) return 'Salvando...';
              return isEditMode ? 'Salvar alterações' : 'Cadastrar';
            })()}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

PointFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['gps', 'beacon']).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })).isRequired,
  onCreated: PropTypes.func,
  pointToEdit: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    categoryId: PropTypes.string,
    description: PropTypes.string,
    radius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    beaconId: PropTypes.string,
    latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isVisible: PropTypes.bool,
    isAutomaticallyPlayed: PropTypes.bool,
    linkURL: PropTypes.string,
    linkTitle: PropTypes.string,
    audioURL: PropTypes.string,
    imageURL: PropTypes.string,
  }),
  onSaved: PropTypes.func,
  initialCoords: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
};
